import { useEffect, useRef } from 'react'
import { Avatar, Box, Grid, ListItem, ListItemButton, Stack, Typography, useTheme } from '@mui/material'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { Security, SecurityCategory_CategoryCode, securityCategory_CategoryCodeToJSON } from '../../proto/generated/security'
import { LOGOS_BASE_URL } from '../../assets/constants'
import { getColorForSymbol } from '../../util/general'
import TextHighlighter from '../common/TextHighlighter'
import { ExtraSmallButtonHoverable } from '../custom'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import type { CompareSecurityPlacement } from './typings'

const safeString = (v: unknown): string => (typeof v === 'string' ? v : '')

const compareActionButtonSx = {
    textTransform: 'none' as const,
    fontSize: 11,
    minHeight: 10,
    height: '26px !important',
    padding: '2px 6px'
}

export interface CompareSecurityRowProps {
    security: Security
    searchTerm: string
    focused: boolean
    onPick: (placement: CompareSecurityPlacement) => void
}

const CompareSecurityRow = ({ security, searchTerm, focused, onPick }: CompareSecurityRowProps) => {
    const theme = useTheme()
    const targetRef = useRef<HTMLLIElement>(null)

    const symbol = security.symbol || ''
    const displayName = security.displayName || ''
    const series = security.series
    const categoryCode = security.categoryCode || SecurityCategory_CategoryCode.CATEGORY_CODE_UNKNOWN
    const exchange = security.exchange ?? Exchange.NSE
    const logoUrl = security.logoUrl

    useEffect(() => {
        if (focused && targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
        }
    }, [focused])

    return (
        <ListItem
            disablePadding
            className={'security-item compare-security-row'}
            sx={(t) => ({
                borderBottom: 0.5,
                borderColor: 'divider',
                '& .compare-security-actions': {
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: t.transitions.create('opacity', { duration: t.transitions.duration.shorter })
                },
                '&:hover .compare-security-actions, &:focus-within .compare-security-actions': {
                    opacity: 1,
                    pointerEvents: 'auto'
                },
                ...(focused
                    ? {
                          '& .compare-security-actions': {
                              opacity: 1,
                              pointerEvents: 'auto'
                          }
                      }
                    : {})
            })}
            onClick={() => {
                AnalyticsManager.logEvent('Symbol Clicked', { source: 'compare-security-picker', symbol })
                onPick('same_percentage_scale')
            }}
            ref={targetRef}
        >
            <ListItemButton disableGutters={true} selected={focused}>
                <Grid container justifyContent={'center'} spacing={1}>
                    <Grid item xs={4}>
                        <Stack direction="row" spacing={1} sx={{ ml: 3 }}>
                            {logoUrl ? (
                                <Avatar alt={safeString(displayName)} src={`${LOGOS_BASE_URL}${logoUrl}.svg`} sx={{ width: 24, height: 24 }} />
                            ) : (
                                <Avatar
                                    alt={safeString(displayName)}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        fontSize: 12,
                                        color: 'white',
                                        bgcolor: getColorForSymbol(symbol, theme.palette.background.default)
                                    }}
                                >
                                    {(symbol || '')
                                        .split(' ')
                                        .map((word) => word[0]?.toUpperCase())
                                        .slice(0, 2)
                                        .join('')}
                                </Avatar>
                            )}
                            <Typography noWrap variant={'body1'} sx={{ lineHeight: '24px' }}>
                                <TextHighlighter textToHighlight={safeString(symbol)} searchTerm={searchTerm} />
                                {series && series !== 'EQ' && series !== 'XX' ? '-' + series : ''}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={5} sx={{ minWidth: 0 }}>
                        <Typography sx={{ lineHeight: '24px' }} variant={'body2'} noWrap>
                            <TextHighlighter textToHighlight={safeString(displayName)} searchTerm={searchTerm} />
                        </Typography>
                    </Grid>
                    <Grid
                        item
                        xs={3}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexWrap: 'nowrap',
                            gap: 0.5,
                            minWidth: 0,
                            pr: 1
                        }}
                    >
                        <Box
                            className="compare-security-actions"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                        >
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="nowrap">
                                <ExtraSmallButtonHoverable
                                    variant={'outlined'}
                                    color={'inherit'}
                                    size={'small'}
                                    sx={compareActionButtonSx}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onPick('same_percentage_scale')
                                    }}
                                >
                                    Same % scale
                                </ExtraSmallButtonHoverable>
                                <ExtraSmallButtonHoverable
                                    variant={'outlined'}
                                    color={'inherit'}
                                    size={'small'}
                                    sx={compareActionButtonSx}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onPick('new_pane')
                                    }}
                                >
                                    New pane
                                </ExtraSmallButtonHoverable>
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 0.5 }}>
                            <Typography
                                component="span"
                                color={'textSecondary'}
                                noWrap
                                variant={'body2'}
                                sx={{
                                    fontSize: 12,
                                    lineHeight: '24px'
                                }}
                            >
                                {(securityCategory_CategoryCodeToJSON(categoryCode) ?? '').toLowerCase()}
                            </Typography>
                            <Typography component="span" noWrap variant={'body2'} sx={{ lineHeight: '24px' }}>
                                {exchangeToJSON(exchange)}
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </ListItemButton>
        </ListItem>
    )
}

export default CompareSecurityRow
